'use client';

import { Button } from '../button';
import { Field } from '../field';
import { IconButton } from '../icon-button';
import { Input } from '../input';
import { Tooltip } from '../tooltip';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import { HelpCircleIcon, Add01Icon, Delete02Icon } from '@terra/ui-icons';

export type KeyValueItem = {
  key: string;
  value: string;
};
export type KeyValueSectionProps = {
  label: string;
  keyPlaceholder: string;
  valuePlaceholder: string;
  addLabel: string;
  deleteAriaLabel: string;
  tooltip?: string;
  items: KeyValueItem[];
  onItemChange: (
    index: number,
    field: keyof KeyValueItem,
    value: string,
  ) => void;
  onAdd: () => void;
  onRemove: (index: number) => void;
  maskValues?: boolean;
};
export const KeyValueSection = ({
  label,
  keyPlaceholder,
  valuePlaceholder,
  addLabel,
  deleteAriaLabel,
  tooltip,
  items,
  onItemChange,
  onAdd,
  onRemove,
  maskValues = false,
}: KeyValueSectionProps) => {
  const hasEmptyPair = items.some(
    (pair) => !pair.key.trim() || !pair.value.trim(),
  );

  return (
    <VStack alignItems="stretch" gap={2} w="full">
      <HStack gap={1} alignItems="center">
        <Field.Label fontSize="xs">{label}</Field.Label>
        {tooltip && (
          <Tooltip title={tooltip} positioning={{ placement: 'top' }}>
            <HelpCircleIcon size={16} cursor="pointer" />
          </Tooltip>
        )}
      </HStack>
      {items.map((pair, index) => (
        <HStack key={index} gap={2} alignItems="flex-end" w="full">
          <Box flex={1}>
            <Input
              size="sm"
              placeholder={keyPlaceholder}
              value={pair.key}
              onChange={(e) => onItemChange(index, 'key', e.target.value)}
            />
          </Box>
          <Box flex={1}>
            <Input
              size="sm"
              placeholder={valuePlaceholder}
              type={maskValues ? 'password' : 'text'}
              value={pair.value}
              onChange={(e) => onItemChange(index, 'value', e.target.value)}
            />
          </Box>
          <IconButton
            variant="ghost"
            size="xs"
            aria-label={deleteAriaLabel}
            disabled={items.length <= 1}
            onClick={() => onRemove(index)}
          >
            <Delete02Icon size={16} />
          </IconButton>
        </HStack>
      ))}
      <Button
        variant="ghost"
        size="xs"
        alignSelf="flex-start"
        disabled={hasEmptyPair}
        onClick={onAdd}
      >
        <Add01Icon size={16} />
        {addLabel}
      </Button>
    </VStack>
  );
};
